import { Box, ButtonBase } from '@mui/material';

import { ElevatorNames } from '~/components/tasks/ElevatorNames';
import {
  avatarColor,
  formatShortDate,
  initials,
  isFollowUpOverdue,
} from '~/components/tasks/taskStyle';
import type { TaskSummary } from '~/data/generated/model';
import { SURFACE_RADIUS } from '~/theme';

export function TaskCard({
  task,
  onOpen,
  showProperty = false,
  highlighted = false,
}: {
  task: TaskSummary;
  onOpen: () => void;
  showProperty?: boolean;
  highlighted?: boolean;
}) {
  const owner = task.assignee?.name ?? 'Unassigned';
  const overdue = isFollowUpOverdue(task);
  return (
    <ButtonBase
      component="div"
      aria-label={`${task.displayId} ${task.title}`}
      onClick={onOpen}
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: highlighted ? '#9fb8e8' : 'divider',
        boxShadow: highlighted ? '0 6px 18px rgba(20,40,90,.10)' : undefined,
        borderRadius: `${SURFACE_RADIUS}px`,
        p: '12px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        textAlign: 'left',
        font: 'inherit',
        gap: '8px',
        transition: 'box-shadow .15s, border-color .15s',
        '&:hover': { boxShadow: '0 6px 18px rgba(20,40,90,.10)', borderColor: '#9fb8e8' },
      }}
    >
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}
      >
        <Box component="span" sx={{ font: "11px 'Roboto Mono', monospace", color: '#777' }}>
          {task.displayId}
        </Box>
      </Box>
      <Box
        sx={{
          fontSize: 14,
          lineHeight: 1.35,
          fontWeight: 500,
          color: '#1b1f24',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textWrap: 'pretty',
        }}
      >
        {task.title}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          fontSize: 12,
          color: '#5f6b78',
          minWidth: 0,
        }}
      >
        {showProperty ? (
          <Box
            component="span"
            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {task.propertyName}
          </Box>
        ) : null}
        <ElevatorNames cars={task.cars} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          borderTop: '1px solid #eef0f3',
          pt: '8px',
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 22,
            height: 22,
            flex: 'none',
            borderRadius: '50%',
            bgcolor: avatarColor(task.assignee?.id),
            color: '#fff',
            fontSize: 9,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {initials(task.assignee?.name)}
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            fontSize: 12,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {owner}
        </Box>
        {task.followUpDate ? (
          <Box
            component="span"
            aria-label={overdue ? 'Follow-up overdue' : 'Follow-up'}
            sx={{
              fontSize: 12,
              fontWeight: overdue ? 600 : 400,
              color: overdue ? '#c62828' : '#5f6b78',
              whiteSpace: 'nowrap',
            }}
          >
            {formatShortDate(task.followUpDate)}
          </Box>
        ) : null}
      </Box>
    </ButtonBase>
  );
}
